/**
 * レシートカスタマイズ設定コンポーネント
 * ロゴ・QRコード・フッターメッセージの設定
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Preview,
  QrCode,
  Image as ImageIcon,
  TextFields,
  Refresh,
  Save,
} from '@mui/icons-material';
import api from '../../services/api';

const ReceiptCustomization = () => {
  const { storeId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = React.useRef(null);

  const [settings, setSettings] = useState({
    // ヘッダーロゴ
    headerLogoEnabled: true,
    headerLogoUrl: '',
    headerLogoWidth: 200,
    headerLogoHeight: 80,

    // QRコード設定
    qrCodeEnabled: true,
    qrCodePosition: 'footer', // header, footer
    qrCodeUrl: '',
    qrCodeSize: 100,
    qrCodeLabel: 'SNSをフォローしてね！',

    // 追加QRコード（クーポン等）
    couponQrEnabled: false,
    couponQrUrl: '',
    couponQrLabel: '次回使えるクーポン',

    // フッターメッセージ
    footerEnabled: true,
    footerLine1: 'ご来店ありがとうございました',
    footerLine2: 'またのお越しをお待ちしております',
    footerLine3: '',

    // 店舗情報表示
    showStoreName: true,
    showStoreAddress: true,
    showStorePhone: true,
    showRegistrationNumber: true,

    // レシート設定
    receiptWidth: 80, // 58 or 80
    fontSize: 'medium',
    printLogo: true,
  });

  // 設定取得
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/receipt-customization`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (err) {
      console.error('レシート設定の取得に失敗:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // 設定保存
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await api.put(`/api/stores/${storeId}/receipt-customization`, settings);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('保存に失敗しました: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ロゴアップロード
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // ファイルサイズチェック (1MB以下)
    if (file.size > 1024 * 1024) {
      setError('ファイルサイズは1MB以下にしてください');
      return;
    }

    // ファイル形式チェック
    if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
      setError('PNG、JPEG、GIF形式のみ対応しています');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post(`/api/stores/${storeId}/receipt-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSettings(prev => ({
        ...prev,
        headerLogoUrl: response.data.url || URL.createObjectURL(file),
      }));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      // デモ用：ローカルプレビュー
      setSettings(prev => ({
        ...prev,
        headerLogoUrl: URL.createObjectURL(file),
      }));
    }
  };

  // ロゴ削除
  const handleLogoDelete = () => {
    setSettings(prev => ({ ...prev, headerLogoUrl: '' }));
  };

  // QRコードプレビュー生成URL
  const getQrPreviewUrl = (url) => {
    if (!url) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(url)}`;
  };

  // レシートプレビュー
  const ReceiptPreview = () => (
    <Paper
      sx={{
        width: settings.receiptWidth === 58 ? 200 : 280,
        p: 2,
        bgcolor: '#fff',
        fontFamily: 'monospace',
        fontSize: settings.fontSize === 'small' ? 10 : settings.fontSize === 'large' ? 14 : 12,
        mx: 'auto',
        border: '1px dashed #ccc',
      }}
    >
      {/* ヘッダーロゴ */}
      {settings.headerLogoEnabled && settings.headerLogoUrl && (
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <img
            src={settings.headerLogoUrl}
            alt="店舗ロゴ"
            style={{
              maxWidth: settings.headerLogoWidth,
              maxHeight: settings.headerLogoHeight,
            }}
          />
        </Box>
      )}

      {/* 店舗名 */}
      {settings.showStoreName && (
        <Typography align="center" sx={{ fontWeight: 'bold', mb: 1, fontFamily: 'inherit' }}>
          サンプル店舗
        </Typography>
      )}

      {/* 店舗情報 */}
      {settings.showStoreAddress && (
        <Typography align="center" variant="caption" display="block" sx={{ fontFamily: 'inherit' }}>
          〒123-4567 東京都渋谷区1-2-3
        </Typography>
      )}
      {settings.showStorePhone && (
        <Typography align="center" variant="caption" display="block" sx={{ fontFamily: 'inherit' }}>
          TEL: 03-1234-5678
        </Typography>
      )}

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      {/* サンプル明細 */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>商品A × 2</span>
          <span>¥1,200</span>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>商品B × 1</span>
          <span>¥800</span>
        </Box>
      </Box>

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      {/* 合計 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
        <span>合計</span>
        <span>¥2,000</span>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em' }}>
        <span>（税込）</span>
        <span>内消費税 ¥182</span>
      </Box>

      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

      {/* QRコード */}
      {settings.qrCodeEnabled && settings.qrCodeUrl && (
        <Box sx={{ textAlign: 'center', my: 1 }}>
          {settings.qrCodeLabel && (
            <Typography variant="caption" display="block" sx={{ fontFamily: 'inherit', mb: 0.5 }}>
              {settings.qrCodeLabel}
            </Typography>
          )}
          <img
            src={getQrPreviewUrl(settings.qrCodeUrl)}
            alt="QRコード"
            style={{ width: settings.qrCodeSize, height: settings.qrCodeSize }}
          />
        </Box>
      )}

      {/* クーポンQR */}
      {settings.couponQrEnabled && settings.couponQrUrl && (
        <Box sx={{ textAlign: 'center', my: 1 }}>
          {settings.couponQrLabel && (
            <Typography variant="caption" display="block" sx={{ fontFamily: 'inherit', mb: 0.5 }}>
              {settings.couponQrLabel}
            </Typography>
          )}
          <img
            src={getQrPreviewUrl(settings.couponQrUrl)}
            alt="クーポンQR"
            style={{ width: 80, height: 80 }}
          />
        </Box>
      )}

      {/* フッターメッセージ */}
      {settings.footerEnabled && (
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          {settings.footerLine1 && (
            <Typography variant="caption" display="block" sx={{ fontFamily: 'inherit' }}>
              {settings.footerLine1}
            </Typography>
          )}
          {settings.footerLine2 && (
            <Typography variant="caption" display="block" sx={{ fontFamily: 'inherit' }}>
              {settings.footerLine2}
            </Typography>
          )}
          {settings.footerLine3 && (
            <Typography variant="caption" display="block" sx={{ fontFamily: 'inherit' }}>
              {settings.footerLine3}
            </Typography>
          )}
        </Box>
      )}

      {/* 登録番号 */}
      {settings.showRegistrationNumber && (
        <Typography
          align="center"
          variant="caption"
          display="block"
          sx={{ fontFamily: 'inherit', mt: 1 }}
        >
          登録番号: T1234567890123
        </Typography>
      )}
    </Paper>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ImageIcon />
        レシートカスタマイズ
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          保存しました
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 設定パネル */}
        <Grid item xs={12} md={7}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab icon={<ImageIcon />} label="ロゴ" />
            <Tab icon={<QrCode />} label="QRコード" />
            <Tab icon={<TextFields />} label="メッセージ" />
          </Tabs>

          {/* ロゴ設定 */}
          {activeTab === 0 && (
            <Card>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.headerLogoEnabled}
                      onChange={(e) => setSettings({ ...settings, headerLogoEnabled: e.target.checked })}
                    />
                  }
                  label="ヘッダーロゴを表示"
                />

                {settings.headerLogoEnabled && (
                  <>
                    <Box sx={{ my: 2 }}>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleLogoUpload}
                      />

                      {settings.headerLogoUrl ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <img
                            src={settings.headerLogoUrl}
                            alt="ロゴプレビュー"
                            style={{
                              maxWidth: 300,
                              maxHeight: 150,
                              border: '1px solid #ddd',
                              borderRadius: 4,
                            }}
                          />
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              startIcon={<CloudUpload />}
                              onClick={() => fileInputRef.current?.click()}
                              sx={{ mr: 1 }}
                            >
                              変更
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={handleLogoDelete}
                            >
                              削除
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
                          <Typography color="text.secondary">
                            クリックしてロゴをアップロード
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            PNG, JPEG, GIF（1MB以下）
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="ロゴ幅（px）"
                          value={settings.headerLogoWidth}
                          onChange={(e) => setSettings({ ...settings, headerLogoWidth: parseInt(e.target.value) })}
                          inputProps={{ min: 50, max: 400 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="ロゴ高さ（px）"
                          value={settings.headerLogoHeight}
                          onChange={(e) => setSettings({ ...settings, headerLogoHeight: parseInt(e.target.value) })}
                          inputProps={{ min: 20, max: 200 }}
                        />
                      </Grid>
                    </Grid>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      推奨サイズ: 80mmレシート → 幅400px × 高さ150px
                    </Alert>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* QRコード設定 */}
          {activeTab === 1 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  メインQRコード（SNS・Webサイト等）
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.qrCodeEnabled}
                      onChange={(e) => setSettings({ ...settings, qrCodeEnabled: e.target.checked })}
                    />
                  }
                  label="QRコードを表示"
                />

                {settings.qrCodeEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label="QRコードのURL"
                      value={settings.qrCodeUrl}
                      onChange={(e) => setSettings({ ...settings, qrCodeUrl: e.target.value })}
                      placeholder="https://instagram.com/yourshop"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="QRコード上のラベル"
                      value={settings.qrCodeLabel}
                      onChange={(e) => setSettings({ ...settings, qrCodeLabel: e.target.value })}
                      placeholder="SNSをフォローしてね！"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="QRコードサイズ（px）"
                      value={settings.qrCodeSize}
                      onChange={(e) => setSettings({ ...settings, qrCodeSize: parseInt(e.target.value) })}
                      inputProps={{ min: 50, max: 200 }}
                    />
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" gutterBottom>
                  クーポンQRコード
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.couponQrEnabled}
                      onChange={(e) => setSettings({ ...settings, couponQrEnabled: e.target.checked })}
                    />
                  }
                  label="クーポンQRコードを表示"
                />

                {settings.couponQrEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label="クーポンページURL"
                      value={settings.couponQrUrl}
                      onChange={(e) => setSettings({ ...settings, couponQrUrl: e.target.value })}
                      placeholder="https://yourshop.com/coupon"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="クーポンラベル"
                      value={settings.couponQrLabel}
                      onChange={(e) => setSettings({ ...settings, couponQrLabel: e.target.value })}
                      placeholder="次回使えるクーポン"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* メッセージ設定 */}
          {activeTab === 2 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  フッターメッセージ
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.footerEnabled}
                      onChange={(e) => setSettings({ ...settings, footerEnabled: e.target.checked })}
                    />
                  }
                  label="フッターメッセージを表示"
                />

                {settings.footerEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label="1行目"
                      value={settings.footerLine1}
                      onChange={(e) => setSettings({ ...settings, footerLine1: e.target.value })}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="2行目"
                      value={settings.footerLine2}
                      onChange={(e) => setSettings({ ...settings, footerLine2: e.target.value })}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="3行目（任意）"
                      value={settings.footerLine3}
                      onChange={(e) => setSettings({ ...settings, footerLine3: e.target.value })}
                    />
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" gutterBottom>
                  店舗情報表示
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.showStoreName}
                          onChange={(e) => setSettings({ ...settings, showStoreName: e.target.checked })}
                        />
                      }
                      label="店舗名"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.showStoreAddress}
                          onChange={(e) => setSettings({ ...settings, showStoreAddress: e.target.checked })}
                        />
                      }
                      label="住所"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.showStorePhone}
                          onChange={(e) => setSettings({ ...settings, showStorePhone: e.target.checked })}
                        />
                      }
                      label="電話番号"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.showRegistrationNumber}
                          onChange={(e) => setSettings({ ...settings, showRegistrationNumber: e.target.checked })}
                        />
                      }
                      label="登録番号"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* 保存ボタン */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              設定を保存
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchSettings}
            >
              リセット
            </Button>
          </Box>
        </Grid>

        {/* プレビューパネル */}
        <Grid item xs={12} md={5}>
          <Card sx={{ position: 'sticky', top: 16 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Preview />
                レシートプレビュー
              </Typography>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <ReceiptPreview />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReceiptCustomization;
